/* eslint-disable no-underscore-dangle */
import React from 'react'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { DatePickerProps } from 'src/antd'
import { css } from 'styled-components'

import { useDesignMode } from 'src/design-mode/useDesignMode'
import { gray600 } from 'src/styles/color-theme'

import { Typography } from './Typography'

/**
 * The house date-panel treatment: an ISO week-number column down the left of
 * the day grid. Every `WhDatePicker` / `WhDatePicker.RangePicker` in the app
 * gets it (59 tags), and nothing else does.
 *
 * IT IS REACHED TWO DIFFERENT WAYS, one per design mode, because antd and the
 * shadcn seam disagree about whether such a column exists at all:
 *
 * - antd 5.15.4's DatePicker never surfaced rc-picker's `showWeekNumber`, so
 *   the column has always been a `dateRender` HACK — an absolutely-positioned
 *   div injected into the first cell of each row, pulled left out of the grid
 *   by a negative margin. That is what ships today and it is untouched here.
 * - `src/antd/DatePicker`'s shadcn half renders a REAL week-number column
 *   (react-day-picker's), so it takes `showWeekNumber` and the hack is not
 *   only unnecessary there but wrong: `cellRender` would put the injected div
 *   inside a `[data-slot='picker-cell-button']` that already lays its own
 *   content out.
 *
 * The number itself is the same in both — `dayjs(...).endOf('week').isoWeek()`,
 * computed here for antd and in `src/antd/DatePicker/panel.tsx` for shadcn.
 *
 * Hence a HOOK rather than the plain function this used to be: the mode has to
 * be SUBSCRIBED to, not merely read. `WhDatePicker` computes these props and
 * hands them down, so without a subscription here a toggle flip would
 * re-render the dispatcher (which does subscribe) while leaving it holding the
 * other mode's props.
 *
 * The styles below are added to the storybook global styles automatically, so
 * there is nothing to wire up at a call site (src/storybook/styles.tsx). They
 * carry BOTH renderings: the `.ant-picker-*` rules the antd branch needs, and
 * a `[data-slot='picker-*']` twin beside each for the shadcn one — the same
 * shape every other port in this track used, because antd's stylesheet is
 * unlayered and stays on the page once anything has rendered an antd picker.
 */
export const GetDatePickerBasePropsGlobalStyles = css`
  .whf-ant-date-picker-base {
    & .ant-picker-date-panel {
      width: 304px;
    }

    & .ant-picker-content {
      margin-left: 28px;
    }

    & .ant-picker-week-number {
      display: flex;
      justify-content: center;
      align-items: center;
      position: absolute;
      margin-left: -28px;
      width: 24px;
      height: 24px;
      cursor: default;
      pointer-events: none;
    }

    /*
     * The shadcn twin. react-day-picker renders the week number as a real
     * cell in the grid, so there is no absolute positioning and no gutter to
     * carve out — only the column's colour and inertness to state.
     */
    & [data-slot='picker-panel'] {
      min-width: 304px;
    }

    & [data-slot='picker-week-number'] {
      color: ${gray600};
      cursor: default;
      pointer-events: none;
      user-select: none;
    }
  }
`

type DatePickerBaseProps = Pick<
  DatePickerProps,
  'dateRender' | 'popupClassName' | 'showWeekNumber'
>

export const useDatePickerBaseProps = ({
  popupClassName,
}: Pick<DatePickerProps, 'popupClassName'>): DatePickerBaseProps => {
  const designMode = useDesignMode()
  const popup = classNames(popupClassName, 'whf-ant-date-picker-base')

  if (designMode === 'shadcn')
    return { showWeekNumber: true, popupClassName: popup }

  const firstDayOfWeek = dayjs().localeData().firstDayOfWeek()
  return {
    dateRender: (current) => {
      return (
        <>
          {current.day() === firstDayOfWeek && (
            <div className="ant-picker-week-number">
              <Typography style={{ color: gray600 }}>
                {dayjs(current).endOf('week').isoWeek()}
              </Typography>
            </div>
          )}
          <div className="ant-picker-cell-inner">{current.date()}</div>
        </>
      )
    },
    popupClassName: popup,
  }
}
